import {Button} from '@chekinapp/ui';
import {useRef, Fragment} from 'react';
import {useTranslation} from 'react-i18next';
import {InfoIcon} from 'lucide-react';
import {Alert, cn, MainLoader} from '@chekin/guestapp-ui-kit';
import {useGuest, useGuestGroup, useIVComputedDetails} from 'hooks';
import {useRemoteIVFlow} from 'hooks/iv/ivRemoteFlow';
import {useSummary} from 'context/summary';
import {useGetGuestId} from '../../../../hooks/useGetGuestId';
import {useReservation} from 'context/reservation';
import {useLayoutMode} from 'hooks/useLayoutMode';
import {useGetVerificationSteps} from './useGetVerificationSteps';
import {useVerificationSettings} from 'hooks/iv/useVerificationSettings';
import {COUNTRY_CODES, IV_PROCESS_TYPE, SDK_MODE} from '@chekin/core';
import {ANALYTIC_EVENTS, Analytics} from '@chekin/shared';
import ChekinProSettings from '../../../../ChekinProSettings';
import VerticalLineIcon from '../../../../assets/icons/vertical-line.svg?react';
import VerticalLineKioskoIcon from '../../../../assets/icons/vertical-long-line.svg?react';
import {SkipIVModal, SkipIVModalRef} from 'components/common/SkipIVModal';
import {VerificationStepItem} from './VerificationStepItem';
import {Wrapper, Text, InfoBox, VerificationSteps} from './styled';
import {ModalTwoButtonsWrapper} from '@guestapp/old-ui-kit';

function StartView() {
  const {t} = useTranslation();
  const id = useGetGuestId();
  const {isLoading} = useGuest({id});

  const {biomatch, isDataLoaded} = useIVComputedDetails();

  const skipIVModalRef = useRef<SkipIVModalRef>(null);
  const openSkipModal = () => skipIVModalRef.current?.open();
  const {countryCode} = useSummary();
  const {housing} = useReservation();
  const {
    isEmailEnabled,
    isPhoneEnabled,
    isLoading: isSettingsLoading,
    isNeedVerification,
  } = useVerificationSettings(housing?.id);
  const layoutMode = useLayoutMode();

  const {guestGroup} = useGuestGroup();

  const isExpectedLeaderGuest = !guestGroup?.added_number_of_guests;
  const isAgeInformationVisible =
    layoutMode === 'kiosko' ||
    (countryCode && countryCode !== COUNTRY_CODES.uae && !isExpectedLeaderGuest);

  const {startVerification} = useRemoteIVFlow(isNeedVerification);

  const getDescriptionItems = useGetVerificationSteps({
    biomatch,
    isLivenessDetectionEnabled: housing?.is_liveness_detection_enabled,
    isEmailEnabled,
    isPhoneEnabled,
    isNeedContactsVerification: isNeedVerification,
  });

  return (
    <Wrapper className="iv-start-view kiosko:w-full">
      <Text className="iv-start-view__text kiosko:text-kiosko-m kiosko:max-w-full kiosko:font-light kiosko:leading-[68px] kiosko:mb-14">
        {t('verify_you_identity_to_continue')}
      </Text>
      <InfoBox
        className={cn(
          'iv-start-view__info-box kiosko:h-[560px] kiosko:max-w-full kiosko:rounded-[36px]',
          'kiosko:p-0 kiosko:mb-[112px]',
        )}
      >
        {isLoading || isSettingsLoading || !isDataLoaded ? (
          <MainLoader visible width={60} />
        ) : (
          <VerificationSteps>
            {getDescriptionItems.map((step, index, arr) => (
              <Fragment key={`step-${index}`}>
                <VerificationStepItem icon={step.icon} text={t(step.text)} />

                {index < arr.length - 1 && (
                  <VerificationStepItem
                    icon={
                      layoutMode === 'kiosko' ? (
                        <VerticalLineKioskoIcon />
                      ) : (
                        <VerticalLineIcon />
                      )
                    }
                    className="kiosko:w-full kiosko:h-[115px] kiosko:mt-4"
                  />
                )}
              </Fragment>
            ))}
          </VerificationSteps>
        )}
      </InfoBox>

      {isAgeInformationVisible && (
        <Alert
          className={cn(
            'text-[var(--primary)] [--lucide-stroke-width:3] max-w-[400px] text-left flex gap-3 mb-6',
            'kiosko:max-w-full kiosko:p-[18px_38px_18px_18px] kiosko:text-kiosko-s kiosko:leading-[56px]',
            'kiosko:rounded-[24px] kiosko:mb-14',
          )}
        >
          <div className="kiosko:p-1">
            <InfoIcon className="h-5 w-5 kiosko:h-10 kiosko:w-10" />
          </div>
          <span className="kiosko:pl-6">{t('minors_under_18_age')}</span>
        </Alert>
      )}

      <ModalTwoButtonsWrapper
        className={cn('iv-start-view__buttons', 'kiosko:gap-y-[30px]')}
      >
        <Button
          onClick={() => {
            void startVerification();
            Analytics.track(ANALYTIC_EVENTS.ivStarted, {
              type: biomatch ? 'Biomatch' : 'OCR',
            });
          }}
          disabled={isLoading}
          className={cn('kiosko:mx-auto kiosko:mt-4')}
        >
          {t('start_verification')}
        </Button>
        {ChekinProSettings?.mode !== SDK_MODE.onlyIV &&
          housing?.identity_verification_process_type !== IV_PROCESS_TYPE.onProperty && (
            <Button
              onClick={openSkipModal}
              disabled={isLoading}
              className={cn('kiosko:mx-auto kiosko:mt-4')}
              variant="secondary"
            >
              {t('skip_process')}
            </Button>
          )}
      </ModalTwoButtonsWrapper>
      <SkipIVModal ref={skipIVModalRef} />
    </Wrapper>
  );
}

export {StartView};
